import { apiService } from './api';
import { DashboardStats, OfficerStats, ManagerStats } from '../types/index';

class DashboardService {
  /**
   * Get dashboard statistics for the current user
   */
  async getDashboardStats(): Promise<DashboardStats> {
    try {
      const response = await apiService.get('/dashboard/stats/');
      console.log('Dashboard stats API response:', response);
      
      // The response from apiService.get is already the data object
      // No need to access response.data again
      return response;
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      throw error;
    }
  }

  /**
   * Get statistics for a specific officer
   * If no officerId is provided, returns stats for the current user
   */
  async getOfficerStats(officerId?: number): Promise<OfficerStats> {
    const url = officerId 
      ? `/dashboard/officer-stats/${officerId}/`
      : '/dashboard/officer-stats/';
    const response = await apiService.get(url);
    return response;
  }

  /**
   * Get comprehensive statistics for managers
   * Includes per-officer stats and overall statistics
   */
  async getManagerStats(): Promise<ManagerStats> {
    const response = await apiService.get('/dashboard/manager-stats/');
    return response;
  }

  /**
   * Get statistics for lead managers
   * (Currently same as manager stats, can be extended)
   */
  async getLeadManagerStats(): Promise<ManagerStats> {
    const response = await apiService.get('/dashboard/lead-manager-stats/');
    return response;
  }

  /**
   * Calculate percentage for a metric
   */
  calculatePercentage(value: number, total: number): number {
    if (total === 0) return 0;
    return Math.round((value / total) * 100 * 10) / 10; // Round to 1 decimal place
  }

  /**
   * Get status distribution as percentages
   */
  getStatusDistribution(stats: DashboardStats): {
    pending: number;
    verified: number;
    deleted: number;
  } {
    const total = stats.total_records;
    return {
      pending: this.calculatePercentage(stats.total_pending, total),
      verified: this.calculatePercentage(stats.total_verified, total),
      deleted: this.calculatePercentage(stats.total_deleted, total),
    };
  }

  /**
   * Get applicant type distribution as percentages
   */
  getApplicantTypeDistribution(stats: DashboardStats): {
    individual: number;
    organization: number;
  } {
    const total = stats.total_records;
    return {
      individual: this.calculatePercentage(stats.total_individual, total),
      organization: this.calculatePercentage(stats.total_organization, total),
    };
  }

  /**
   * Get passport type distribution as percentages
   */
  getPassportTypeDistribution(stats: DashboardStats): {
    mrp: number;
    epp: number;
  } {
    const total = stats.total_records;
    return {
      mrp: this.calculatePercentage(stats.total_mrp, total),
      epp: this.calculatePercentage(stats.total_epp, total),
    };
  }

  /**
   * Format currency amount
   */
  formatAmount(amount: number): string {
    return new Intl.NumberFormat('en-JO', {
      style: 'currency',
      currency: 'JOD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  /**
   * Get status summary text
   */
  getStatusSummary(stats: DashboardStats): string {
    const { total_records, total_pending, total_verified, total_deleted } = stats;
    return `${total_records} total records: ${total_pending} pending, ${total_verified} verified, ${total_deleted} deleted`;
  }

  /**
   * Check if iqamas are expiring soon
   */
  hasExpiringIqamas(stats: DashboardStats): boolean {
    return stats.expiring_iqamas_30_days > 0;
  }

  /**
   * Get alert level based on expiring iqamas
   */
  getExpiringIqamasAlertLevel(count: number): 'success' | 'warning' | 'danger' {
    if (count === 0) return 'success';
    if (count <= 5) return 'warning';
    return 'danger';
  }

  /**
   * Get performance metrics
   */
  getPerformanceMetrics(stats: DashboardStats): {
    verificationRate: number;
    todayProductivity: number;
    pendingRate: number;
  } {
    const total = stats.total_records;
    return {
      verificationRate: stats.verification_rate,
      todayProductivity: this.calculatePercentage(stats.total_today_records, total),
      pendingRate: this.calculatePercentage(stats.total_pending, total),
    };
  }
}

export const dashboardService = new DashboardService();